import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, HostListener, Input, OnDestroy, inject } from '@angular/core';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartWrapperInstance, ChartWrapperService } from '../../shared-services/chart-wrapper.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableAxisLabel, buildConfigurableTooltipFormatter } from '../../utils/chart.utils';
import { ColorConfig, getDefaultColor, getSeriesColor } from '../../utils/color.utils';
import {
  buildSecondaryXAxisOption,
  buildSecondaryYAxisOption,
  getDoubleAxisGridAdjustments,
} from '../../utils/axis.utils';

interface StackedLineSeries {
  name: string;
  data: number[];
  stack?: string;
}

@Component({
  selector: 'app-stacked-line-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './stacked-line-chart.component.html',
  styleUrl: './stacked-line-chart.component.scss',
})
export class StackedLineChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  protected myChart!: echarts.ECharts;
  protected allSeriesNames: string[] = [];
  protected allCategories: string[] = [];

  protected wrapper: ChartWrapperInstance = inject(ChartWrapperService).createInstance();
  private configService = inject(ConfigService);
  private responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  private reflowSub?: Subscription;
  private themeSub?: Subscription;
  private lastCategories?: string[];
  private lastSeries?: StackedLineSeries[];

  ngAfterViewInit(): void {
    if (!this.data?.widgetId) return;

    this.wrapper.attach(this.data, {
      onReload: (filters) => this.loadAndRenderChart(filters),
      getChart: () => this.myChart,
      getSeriesNames: () => this.allSeriesNames,
      getCategories: () => this.allCategories,
    });

    this.loadAndRenderChart();

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });

    // Re-render with fresh theme tokens when the user switches theme at runtime.
    // Dispose + re-init so ECharts picks up the updated 'dvm' registered theme.
    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.lastCategories || !this.lastSeries || !this.myChart) return;
      const chartDom = document.getElementById(this.data?.widgetId);
      if (!chartDom) return;
      this.myChart.dispose();
      this.myChart = echarts.init(chartDom, 'dvm');
      this.renderChart(this.lastCategories, this.lastSeries);
    });
  }

  @HostListener('window:resize')
  onResize(): void {
    this.myChart?.resize();
  }

  protected loadAndRenderChart(filters?: any): void {
    const chartDom = document.getElementById(this.data?.widgetId);
    if (!chartDom) return;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    this.myChart.showLoading();

    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res: any) => {
        const parsed = this.extractChartData(res);
        if (!parsed) {
          this.myChart.hideLoading();
          return;
        }

        this.wrapper.setSourceData(
          parsed.rawRows,
          this.data?.categoryKey || 'category',
          this.data?.seriesKey || 'series',
        );

        this.renderChart(parsed.categories, parsed.series);
      },
      error: () => {
        this.myChart?.hideLoading();
      },
    });
  }

  protected renderChart(categories: string[], series: StackedLineSeries[]): void {
    if (!this.myChart || !categories.length || !series.length) {
      this.myChart?.hideLoading();
      return;
    }

    this.lastCategories = categories;
    this.lastSeries = series;
    this.allCategories = categories;
    this.allSeriesNames = series.map((s) => s.name);

    const chartConfig = this.data?.chartConfig || {};
    const tooltipCfg = chartConfig?.tooltip;
    const tooltipFormatter = buildConfigurableTooltipFormatter(tooltipCfg);
    const yAxisLabelCfg = buildConfigurableAxisLabel(chartConfig?.yAxis);
    const gridAdj = getDoubleAxisGridAdjustments(this.data);

    const colorCfg: ColorConfig = {
      colorMap: chartConfig.colorMap || {},
      topNColorsByStackPosition: chartConfig.topNColorsByStackPosition || [],
    };

    const coloredSeries = series.map((s, idx) => {
      let seriesColor = getSeriesColor(idx, s.name, colorCfg) || getDefaultColor(idx);
      seriesColor = this.wrapper.applyColorConsistency(s.name, seriesColor) || seriesColor;

      return {
        name: s.name,
        type: 'line',
        stack: s.stack || this.data?.stackName || 'Total',
        data: s.data,
        boundaryGap: false,
        smooth: chartConfig?.smooth === true,
        showSymbol: true,
        symbol: chartConfig?.symbol || 'circle',
        symbolSize: chartConfig?.symbolSize || 8,
        lineStyle: {
          width: chartConfig?.lineWidth || 2,
          color: seriesColor,
        },
        itemStyle: {
          color: '#ffffff',
          borderColor: seriesColor,
          borderWidth: 2,
        },
        emphasis: { focus: 'series' },
      };
    });

    const primaryXAxis: any = {
      type: 'category',
      boundaryGap: false,
      data: categories,
      triggerEvent: true,
    };
    const secondaryX = buildSecondaryXAxisOption(this.data, categories);
    const xAxisOption = secondaryX ? [primaryXAxis, secondaryX.axis] : primaryXAxis;

    const primaryYAxis: any = {
      type: 'value',
      triggerEvent: true,
      axisLabel: yAxisLabelCfg || {
        formatter: (value: number) => Number(value || 0).toLocaleString('en-US'),
      },
    };
    const secondaryY = buildSecondaryYAxisOption(this.data);
    const yAxisOption = secondaryY ? [primaryYAxis, secondaryY] : primaryYAxis;

    const option: echarts.EChartsOption = {
      title: {
        show: chartConfig?.showInnerTitle !== false,
        text: chartConfig?.innerTitle || this.data?.title || 'Stacked Line',
        left: 'center',
        top: 4,
        textStyle: {
          fontSize: 18,
          fontWeight: 700,
        },
      },
      tooltip: {
        trigger: tooltipCfg?.trigger || 'axis',
        ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
      },
      legend: {
        data: this.allSeriesNames,
        type: 'scroll',
        bottom: 0,
      },
      toolbox: {
        show: chartConfig?.showToolbox !== false,
        right: 8,
        top: 4,
        feature: {
          saveAsImage: {},
        },
      },
      grid: {
        left: '3%',
        right: `${4 + gridAdj.extraRight}%`,
        top: '16%',
        bottom: `${14 + gridAdj.extraBottom}%`,
        containLabel: true,
      },
      xAxis: xAxisOption,
      yAxis: yAxisOption,
      series: coloredSeries as any,
    };

    applyRoleVisibilityToOption(option, this.data);

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.wrapper.afterRender();
    // The widget container is often still growing to its final height when
    // getConfig resolves synchronously (self-service path) — ECharts then draws
    // into a collapsed/short box. Resize across a few settling points (next
    // frame + short timeouts) and observe the container so it expands to fill.
    this.scheduleResizeToContainer();
  }

  /** Re-measure and resize the chart to its container across several settling
   *  ticks, and keep it correct via a one-shot ResizeObserver. Guards against the
   *  synchronous self-service render happening before the grid item is sized. */
  private resizeObserver?: ResizeObserver;
  protected scheduleResizeToContainer(): void {
    const resize = () => this.myChart?.resize();
    requestAnimationFrame(resize);
    setTimeout(resize, 60);
    setTimeout(resize, 300);

    const dom = document.getElementById(this.data?.widgetId);
    if (dom && typeof ResizeObserver !== 'undefined' && !this.resizeObserver) {
      this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
      this.resizeObserver.observe(dom);
    }
  }

  protected extractChartData(
    response: any,
  ): { categories: string[]; series: StackedLineSeries[]; rawRows: any[] } | null {
    if (!response) return null;

    const responsePath = this.data?.responsePath as string | undefined;
    const source = responsePath ? this.getByPath(response, responsePath) : response;
    const resolvedSource = source ?? response;

    // Supports direct ECharts-like payloads: { xAxis: { data: [...] }, series: [...] }
    const directXAxis = resolvedSource?.xAxis?.data || resolvedSource?.category || resolvedSource?.categories;
    if (Array.isArray(directXAxis) && Array.isArray(resolvedSource?.series)) {
      const categories = directXAxis.map((c: any) => String(c));
      const series: StackedLineSeries[] = resolvedSource.series.map((s: any) => ({
        name: s?.name || '',
        stack: s?.stack || this.data?.stackName || 'Total',
        data: Array.isArray(s?.data) ? s.data.map((v: any) => Number(v ?? 0) || 0) : [],
      }));
      const rawRows = this.buildRowsFromSeries(categories, series);
      return { categories, series, rawRows };
    }

    const dataKey = this.data?.dataKey || 'stackedLine';
    const rows = this.pickArrayData(resolvedSource, dataKey) || this.pickArrayData(response, dataKey);
    if (!Array.isArray(rows) || rows.length === 0) return null;

    const categoryKey = this.data?.categoryKey || 'category';
    const seriesKey = this.data?.seriesKey || 'series';
    const valueKey = this.data?.valueKey || 'value';
    const stackKey = this.data?.stackKey || 'stack';

    const categories = [...new Set(rows.map((item: any) => String(item?.[categoryKey] ?? '')))]
      .filter((v) => v !== '');
    const seriesNames = [...new Set(rows.map((item: any) => String(item?.[seriesKey] ?? '')))]
      .filter((v) => v !== '');

    const series: StackedLineSeries[] = seriesNames.map((name) => {
      const rowForStack = rows.find((r: any) => String(r?.[seriesKey] ?? '') === name);
      const stackValue = rowForStack?.[stackKey] || this.data?.stackName || 'Total';
      return {
        name,
        stack: String(stackValue),
        data: categories.map((cat) => {
          const matched = rows.find(
            (item: any) =>
              String(item?.[categoryKey] ?? '') === cat &&
              String(item?.[seriesKey] ?? '') === name,
          );
          return Number(matched?.[valueKey] ?? 0) || 0;
        }),
      };
    });

    return {
      categories,
      series,
      rawRows: rows,
    };
  }

  protected pickArrayData(source: any, dataKey: string): any[] | null {
    if (!source) return null;

    const candidates = [
      source?.[dataKey],
      source?.data?.[dataKey],
      source?.data?.[0]?.[dataKey],
      source?.responseList?.[0]?.data?.[0]?.[dataKey],
      source?.responseList?.[0]?.responseData?.[0]?.[dataKey],
      source?.bar,
      source?.line,
      Array.isArray(source) ? source : null,
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) return candidate;
    }

    return null;
  }

  protected buildRowsFromSeries(categories: string[], series: StackedLineSeries[]): any[] {
    const categoryKey = this.data?.categoryKey || 'category';
    const seriesKey = this.data?.seriesKey || 'series';
    const valueKey = this.data?.valueKey || 'value';
    const stackKey = this.data?.stackKey || 'stack';

    const rows: any[] = [];
    series.forEach((seriesItem) => {
      categories.forEach((category, index) => {
        rows.push({
          [categoryKey]: category,
          [seriesKey]: seriesItem.name,
          [valueKey]: Number(seriesItem.data[index] ?? 0) || 0,
          [stackKey]: seriesItem.stack || this.data?.stackName || 'Total',
        });
      });
    });
    return rows;
  }

  protected getByPath(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((acc: any, token: string) => {
      if (acc === null || acc === undefined) return undefined;
      if (/^\d+$/.test(token)) return acc[Number(token)];
      return acc[token];
    }, obj);
  }

  ngOnDestroy(): void {
    this.wrapper.detach();
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.myChart?.dispose();
  }
}
